// BrandDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from './config.js';
import { useParams, useNavigate } from 'react-router-dom';
import axios from "axios";
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogContent,
  Avatar,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import { 
  ArrowBack, 
  Download, 
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  FolderZip as ZipIcon,
  DownloadForOffline,
  Visibility,
  Close,
} from '@mui/icons-material';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import useAuditPageView from '../hooks/useAuditPageView';

const BrandDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageThumbnails, setImageThumbnails] = useState({});

  useEffect(() => {
    fetchBrandData();
  }, [id]);

  useAuditPageView(brand ? `Viewed brand ${brand.name}` : null);

  const fetchBrandData = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${API_BASE_URL}/api/brands/${id}/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`, // attach access token if needed
          "Content-Type": "application/json",
        },
      });

      const data = response.data;
      setBrand(data);

      // Initialize selected files state
      initializeSelectedFiles(data.additionalFiles);

      // Preload image thumbnails
      preloadImageThumbnails(data.additionalFiles);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Error fetching brand data";
      setError(errorMsg);
      console.error("Error fetching brand data:", errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const initializeSelectedFiles = (files) => {
    const selected = {};
    const groupedFiles = groupFilesByCategory(files);
    
    Object.keys(groupedFiles).forEach(category => {
      selected[category] = groupedFiles[category].map(() => true);
    });
    
    setSelectedFiles(selected);
  };

  const preloadImageThumbnails = (files) => {
    const thumbnails = {};
    
    files?.forEach((file, index) => {
      if (isImageFile(file)) {
        const img = new Image();
        img.src = file.url;
        img.onload = () => {
          setImageThumbnails(prev => ({
            ...prev,
            [index]: file.url
          }));
        };
        img.onerror = () => {
          // If image fails to load, don't set thumbnail
          console.warn(`Failed to load thumbnail for ${file.name}`);
        };
      }
    });
  };

  // Group files by category instead of name
  const groupFilesByCategory = (files) => {
    const grouped = {};
    
    files?.forEach(file => {
      const category = file.category || 'Other Files';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(file);
    });
    
    return grouped;
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleDownloadSingle = async (fileUrl, fileName, category, fileIndex) => {
    try {
      setDownloading(prev => ({ ...prev, [`${category}_${fileIndex}`]: true }));
      window.open(fileUrl, '_blank');
    } finally {
      setTimeout(() => {
        setDownloading(prev => ({ ...prev, [`${category}_${fileIndex}`]: false }));
      }, 1000);
    }
  };

  const handleDownloadCategoryAsZip = async (category, files) => {
    try {
      const downloadKey = `${category}_zip`;
      setDownloading(prev => ({ ...prev, [downloadKey]: true }));
      
      const zip = new JSZip();
      const categoryFolder = zip.folder(category.replace(/\s+/g, '_'));
      
      // Download each selected file and add to zip
      const downloadPromises = files.map(async (file, index) => {
        if (!selectedFiles[category]?.[index]) return; // Skip if not selected
        
        try {
          const response = await fetch(file.url);
          const blob = await response.blob();
          const fileName = file.fileName || file.name || file.url.split('/').pop();
          categoryFolder.file(fileName, blob);
        } catch (error) {
          console.error(`Error downloading ${file.url}:`, error);
        }
      });
      
      await Promise.all(downloadPromises);
      
      // Generate and download zip
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${brand.name}_${category.replace(/\s+/g, '_')}.zip`);
      
    } catch (error) {
      console.error('Error creating zip:', error);
      alert('Failed to create zip file. Please try again.');
    } finally {
      setDownloading(prev => ({ ...prev, [`${category}_zip`]: false }));
    }
  };

  const handleDownloadAllAsZip = async () => {
    try {
      setDownloading(prev => ({ ...prev, all: true }));
      
      const zip = new JSZip();
      const brandFolder = zip.folder(brand.name.replace(/\s+/g, '_'));
      
      const groupedFiles = groupFilesByCategory(brand.additionalFiles);
      const downloadPromises = [];
      
      Object.entries(groupedFiles).forEach(([category, files]) => {
        // Only create category folder if there are selected files in that category
        const selectedFilesInCategory = files.filter((file, index) => selectedFiles[category]?.[index]);
        
        if (selectedFilesInCategory.length > 0) {
          const categoryFolder = brandFolder.folder(category.replace(/\s+/g, '_'));
          
          selectedFilesInCategory.forEach((file) => {
            downloadPromises.push(
              (async () => {
                try {
                  const response = await fetch(file.url);
                  const blob = await response.blob();
                  const fileName = file.fileName || file.name || file.url.split('/').pop();
                  categoryFolder.file(fileName, blob);
                } catch (error) {
                  console.error(`Error downloading ${file.url}:`, error);
                }
              })()
            );
          });
        }
      });
      
      await Promise.all(downloadPromises);
      
      // Generate and download zip
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${brand.name}_All_Files.zip`);
      
    } catch (error) {
      console.error('Error creating zip:', error);
      alert('Failed to create zip file. Please try again.');
    } finally {
      setDownloading(prev => ({ ...prev, all: false }));
    }
  };

  const toggleFileSelection = (category, index) => {
    setSelectedFiles(prev => ({
      ...prev,
      [category]: prev[category].map((selected, i) => 
        i === index ? !selected : selected
      )
    }));
  };

  const toggleAllFilesInCategory = (category, selectAll) => {
    setSelectedFiles(prev => ({
      ...prev,
      [category]: prev[category].map(() => selectAll)
    }));
  };

  const isImageFile = (file) => {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'];
    
    if (file.fileExtension) {
      return imageExtensions.includes(file.fileExtension.toLowerCase());
    }
    
    const extension = file.url?.split('.').pop().toLowerCase();
    return imageExtensions.includes(extension);
  };

  const getFileIcon = (file, index) => {
    if (isImageFile(file)) {
      // Show thumbnail for images
      if (imageThumbnails[index]) {
        return (
          <Avatar
            src={imageThumbnails[index]}
            variant="rounded"
            sx={{ 
              marginRight: 1,
              width: isMobile ? 48 : 56, 
              height: isMobile ? 48 : 56,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedImage(file.url);
              setImagePreviewOpen(true);
            }}
          />
        );
      }
      return <ImageIcon color="primary" />;
    } else if (file.fileExtension === 'pdf') {
      return <PdfIcon color="error" />;
    }
    return <FileIcon />;
  };

  const getSelectedFileCount = () => {
    let count = 0;
    Object.entries(selectedFiles).forEach(([category, selections]) => {
      count += selections.filter(selected => selected).length;
    });
    return count;
  };

  const getTotalFileCount = () => {
    return brand?.additionalFiles?.length || 0;
  };

  // Get all image files for the image gallery view
  const getAllImageFiles = () => {
    if (!brand?.additionalFiles) return [];
    
    return brand.additionalFiles
      .map((file, index) => ({ ...file, index }))
      .filter(file => isImageFile(file));
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !brand) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: isMobile ? 2 : 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mb: 3 }}
          size={isMobile ? "small" : "medium"}
        >
          {isMobile ? "Back" : "Back to Marketing Materials"}
        </Button>
        <Alert severity="error">
          {error || 'Brand not found'}
        </Alert>
      </Container>
    );
  }

  const groupedFiles = groupFilesByCategory(brand.additionalFiles);
  const totalFiles = getTotalFileCount();
  const selectedCount = getSelectedFileCount();
  const imageFiles = getAllImageFiles();

  return (
    <Container maxWidth="lg" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 2 : 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={handleBack}
        sx={{ mb: 3 }}
        size={isMobile ? "small" : "medium"}
      >
        {isMobile ? "Back" : "Back to Marketing Materials"}
      </Button>

      <Grid container spacing={isMobile ? 2 : 4}>
        {/* Logo/Icon Section - Full width on mobile */}
        <Grid item xs={12} md={4}>
          <Box sx={{ 
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: isMobile ? '200px' : '200px'
          }}>
            {brand.imageUrl ? (
              <img
                src={brand.imageUrl}
                alt={`${brand.name} logo`}
                style={{
                  maxWidth: isMobile ? '150px' : '200px',
                  maxHeight: isMobile ? '150px' : '200px',
                  objectFit: 'contain',
                  marginBottom: '16px'
                }}
              />
            ) : (
              <ImageIcon 
                sx={{ 
                  fontSize: isMobile ? 80 : 120,
                  color: 'primary.main',
                  mb: 2
                }} 
              />
            )}
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              gutterBottom
              sx={{ fontSize: isMobile ? '1.5rem' : '2.125rem' }}
            >
              {brand.name}
            </Typography>
            <Stack 
              direction={isMobile ? "column" : "row"} 
              spacing={1} 
              sx={{ mt: 1, alignItems: 'center' }}
            >
              <Chip 
                label={`${totalFiles} files`}
                color="primary"
                variant="outlined"
                size={isMobile ? "small" : "medium"}
              />
              {imageFiles.length > 0 && (
                <Chip 
                  label={`${imageFiles.length} images`}
                  color="secondary"
                  variant="outlined"
                  size={isMobile ? "small" : "medium"}
                  icon={<ImageIcon fontSize={isMobile ? "small" : "medium"} />}
                />
              )}
            </Stack>
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            gutterBottom 
            sx={{ 
              color: 'primary.main',
              fontSize: isMobile ? '1rem' : '1.25rem'
            }}
          >
            About {brand.name}
          </Typography>
          <Typography 
            variant="body1"
            maxWidth="900px" 
            paragraph 
            sx={{ 
              whiteSpace: 'pre-line', 
              mb: 4,
              fontSize: isMobile ? '0.875rem' : '1rem'
            }}
          >
            {brand.description || 'No description available.'}
          </Typography>

          {/* Download All Button */}
          {totalFiles > 0 && (
            <Card sx={{ mb: 3, backgroundColor: 'primary.lighter' }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between', 
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: isMobile ? 2 : 0,
                  mb: 2 
                }}>
                  <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
                    <Typography 
                      variant={isMobile ? "subtitle1" : "h6"} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        fontSize: isMobile ? '1rem' : '1.25rem'
                      }}
                    >
                      <DownloadForOffline fontSize={isMobile ? "small" : "medium"} />
                      Bulk Download
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                    >
                      {selectedCount} of {totalFiles} files selected
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={downloading.all ? 
                      <CircularProgress size={isMobile ? 16 : 20} /> : 
                      <ZipIcon fontSize={isMobile ? "small" : "medium"} />
                    }
                    onClick={handleDownloadAllAsZip}
                    disabled={downloading.all || selectedCount === 0}
                    size={isMobile ? "small" : "large"}
                    fullWidth={isMobile}
                    sx={{ 
                      minWidth: isMobile ? '100%' : 'auto',
                      mt: isMobile ? 1 : 0
                    }}
                  >
                    {downloading.all ? 'Creating ZIP...' : 
                      isMobile ? `Download (${selectedCount})` : 'Download Selected as ZIP'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            gutterBottom 
            sx={{ 
              color: 'primary.main', 
              mb: 3,
              fontSize: isMobile ? '1rem' : '1.25rem'
            }}
          >
            Available Files by Category
          </Typography>

          {/* Files by Category */}
          {Object.entries(groupedFiles).map(([category, files]) => {
            const selectedInCategory = selectedFiles[category]?.filter(Boolean).length || 0;
            const allSelected = selectedInCategory === files.length;
            const someSelected = selectedInCategory > 0 && selectedInCategory < files.length;

            return (
              <Card key={category} sx={{ mb: 3 }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between', 
                    alignItems: isMobile ? 'flex-start' : 'center',
                    mb: 2,
                    gap: isMobile ? 2 : 0
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'flex-start' : 'center', 
                      gap: isMobile ? 1 : 2,
                      width: isMobile ? '100%' : 'auto'
                    }}>
                      <Box>
                        <Typography 
                          variant={isMobile ? "subtitle1" : "h6"} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            flexWrap: 'wrap',
                            fontSize: isMobile ? '1rem' : '1.25rem'
                          }}
                        >
                          {category}
                          <Chip 
                            label={`${files.length} file${files.length > 1 ? 's' : ''}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                        >
                          {selectedInCategory} of {files.length} selected
                        </Typography>
                      </Box>
                      
                      <FormControlLabel
                        control={
                          <Checkbox
                            size={isMobile ? "small" : "medium"}
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={(e) => toggleAllFilesInCategory(category, e.target.checked)}
                          />
                        }
                        label={
                          <Typography 
                            variant={isMobile ? "caption" : "body2"}
                            sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                          >
                            {allSelected ? "Deselect All" : someSelected ? "Some Selected" : "Select All"}
                          </Typography>
                        }
                      />
                    </Box>
                    
                    {files.length > 1 && (
                      <Button
                        variant="outlined"
                        size={isMobile ? "small" : "medium"}
                        startIcon={downloading[`${category}_zip`] ? 
                          <CircularProgress size={isMobile ? 12 : 16} /> : 
                          <ZipIcon fontSize={isMobile ? "small" : "medium"} />
                        }
                        onClick={() => handleDownloadCategoryAsZip(category, files)}
                        disabled={downloading[`${category}_zip`] || selectedInCategory === 0}
                        fullWidth={isMobile}
                        sx={{ 
                          minWidth: isMobile ? '100%' : 'auto',
                          mt: isMobile ? 1 : 0
                        }}
                      >
                        {downloading[`${category}_zip`] ? 'Zipping...' : 
                          isMobile ? `Zip (${selectedInCategory})` : `Download ${selectedInCategory} as ZIP`}
                      </Button>
                    )}
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <List sx={{ p: 0 }}>
                    {files.map((file, index) => {
                      const fileIndex = brand.additionalFiles.findIndex(f => 
                        f.url === file.url && f.name === file.name
                      );
                      
                      return (
                        <React.Fragment key={index}>
                          <ListItem
                            alignItems="flex-start"
                            sx={{
                              flexDirection: isMobile ? 'column' : 'row',
                              alignItems: isMobile ? 'flex-start' : 'center',
                              p: isMobile ? '12px 0' : '16px 0',
                              width: '100%'
                            }}
                            secondaryAction={
                              <Box sx={{ 
                                display: 'flex', 
                                gap: 1,
                                mt: isMobile ? 2 : 0,
                                width: isMobile ? '100%' : 'auto',
                                justifyContent: isMobile ? 'flex-end' : 'flex-start'
                              }}>
                                {file.url && (
                                  <Tooltip title={isImageFile(file) ? "View Full Size" : "Preview"}>
                                    <IconButton
                                      edge="end"
                                      onClick={() => {
                                        if (isImageFile(file)) {
                                          setSelectedImage(file.url);
                                          setImagePreviewOpen(true);
                                        } else {
                                          window.open(file.url, '_blank');
                                        }
                                      }}
                                      size={isMobile ? "small" : "medium"}
                                    >
                                      <Visibility fontSize={isMobile ? "small" : "medium"} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Button
                                  variant="outlined"
                                  size={isMobile ? "small" : "medium"}
                                  startIcon={downloading[`${category}_${index}`] ? 
                                    <CircularProgress size={isMobile ? 12 : 16} /> : 
                                    <Download fontSize={isMobile ? "small" : "medium"} />
                                  }
                                  onClick={() => handleDownloadSingle(file.url, file.name, category, index)}
                                  disabled={downloading[`${category}_${index}`]}
                                  sx={{ 
                                    minWidth: isMobile ? 'auto' : '100px',
                                    flex: isMobile ? 1 : 'none'
                                  }}
                                >
                                  {downloading[`${category}_${index}`] ? 
                                    isMobile ? '...' : 'Downloading...' : 
                                    isMobile ? 'DL' : 'Single'
                                  }
                                </Button>
                              </Box>
                            }
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              width: '100%',
                              alignItems: 'center',
                              mb: isMobile ? 1 : 0
                            }}>
                              <ListItemIcon sx={{ minWidth: isMobile ? 36 : 48 }}>
                                <Checkbox
                                  edge="start"
                                  size={isMobile ? "small" : "medium"}
                                  checked={!!selectedFiles[category]?.[index]}
                                  onChange={() => toggleFileSelection(category, index)}
                                  tabIndex={-1}
                                  disableRipple
                                />
                              </ListItemIcon>
                              <ListItemIcon sx={{ 
                                minWidth: isMobile ? 40 : 56,
                                mr: isMobile ? 1 : 2
                              }}>
                                {getFileIcon(file, fileIndex)}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography 
                                    variant={isMobile ? "body2" : "body1"}
                                    sx={{ 
                                      fontWeight: 'medium',
                                      fontSize: isMobile ? '0.875rem' : '1rem'
                                    }}
                                  >
                                    {file.name || file.fileName || 'Unnamed File'}
                                  </Typography>
                                }
                                secondary={
                                  <Box component="span" sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                                  }}>
                                    {file.uploadedAt && (
                                      <span>Uploaded: {new Date(file.uploadedAt).toLocaleDateString()}</span>
                                    )}
                                    {file.size && (
                                      <span>Size: {file.size}</span>
                                    )}
                                    {isImageFile(file) && (
                                      <span style={{ color: '#1976d2', fontStyle: 'italic' }}>
                                        Image - Tap thumbnail to preview
                                      </span>
                                    )}
                                  </Box>
                                }
                                sx={{ 
                                  m: 0,
                                  '& .MuiListItemText-secondary': {
                                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                                  }
                                }}
                              />
                            </Box>
                          </ListItem>
                          {index < files.length - 1 && <Divider variant="inset" component="li" />}
                        </React.Fragment>
                      );
                    })}
                  </List>
                </CardContent>
              </Card>
            );
          })}

          {/* No files message */}
          {totalFiles === 0 && (
            <Alert severity="info" sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
              No files available for download.
            </Alert>
          )}
        </Grid>
      </Grid>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogContent sx={{ 
          p: 0, 
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: isMobile ? '100vh' : '60vh',
          backgroundColor: 'rgba(0, 0, 0, 0.9)'
        }}>
          <IconButton
            aria-label="close"
            onClick={() => setImagePreviewOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              }
            }}
            size={isMobile ? "small" : "medium"}
          >
            <Close />
          </IconButton>
          
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: isMobile ? 'calc(100vh - 64px)' : '80vh',
                objectFit: 'contain',
                padding: isMobile ? '40px 16px' : '40px'
              }}
              onClick={() => setImagePreviewOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default BrandDetailPage;